import React, { useState } from 'react';
import { Calendar, User, ChevronDown, ChevronRight, ChevronLeft, ChevronFirst, ChevronLast, Eye } from 'lucide-react';
import { format, addMonths, addYears, parseISO } from 'date-fns';
import { Link } from 'react-router-dom';

const PolicyTable = ({ policies }) => {
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [viewMode, setViewMode] = useState('monthly'); // 'monthly' or 'yearly'
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10); // Items per page

  const toggleRow = (policyNumber) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(policyNumber)) {
      newExpanded.delete(policyNumber);
    } else {
      newExpanded.add(policyNumber);
    }
    setExpandedRows(newExpanded);
  };

  // Reset to first page when view mode changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [viewMode]);

  const generatePaymentSchedule = (policy) => {
    const schedule = [];
    const startDate = parseISO(policy.effective_start_date);
    const endDate = policy.effective_end_date ? parseISO(policy.effective_end_date) : null;
    
    let currentDate = startDate;
    let paymentCount = 0;
    
    while (paymentCount < policy.total_period && (!endDate || currentDate <= endDate)) {
      const payment = {
        date: currentDate,
        amount: policy.premium_amount,
        period: paymentCount + 1
      };
      
      schedule.push(payment);
      
      if (policy.premium_frequency === 'monthly') {
        currentDate = addMonths(currentDate, 1);
      } else {
        currentDate = addYears(currentDate, 1);
      }
      
      paymentCount++;
    }
    
    return schedule;
  };

  const aggregatePaymentsByPeriod = (schedule, mode) => {
    const aggregated = {};
    
    schedule.forEach(payment => {
      let key;
      if (mode === 'monthly') {
        key = format(payment.date, 'yyyy-MM');
      } else {
        key = format(payment.date, 'yyyy');
      }
      
      if (!aggregated[key]) {
        aggregated[key] = {
          total: 0,
          count: 0,
          policies: new Set()
        };
      }
      
      aggregated[key].total += payment.amount;
      aggregated[key].count += 1;
      aggregated[key].policies.add(payment.policy_number);
    });
    
    return aggregated;
  };

  const formatCurrency = (amount, currency) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD'
    }).format(amount);
  };

  const formatDate = (date) => {
    return format(parseISO(date), 'MMM dd, yyyy');
  };

  // Pagination logic
  const totalItems = policies.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPolicies = policies.slice(startIndex, endIndex);

  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const goToFirstPage = () => goToPage(1);
  const goToLastPage = () => goToPage(totalPages);
  const goToPreviousPage = () => goToPage(currentPage - 1);
  const goToNextPage = () => goToPage(currentPage + 1);

  return (
    <div className="space-y-4">
      {/* View Mode Toggle */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <span className="text-sm font-medium text-gray-700">View Mode:</span>
          <div className="flex bg-quickbooks-gray rounded-lg p-1">
            <button
              onClick={() => setViewMode('monthly')}
              className={`px-3 py-1 text-sm font-medium rounded-md transition-colors duration-200 ${
                viewMode === 'monthly'
                  ? 'bg-white text-quickbooks-blue shadow-quickbooks'
                  : 'text-quickbooks-gray-dark hover:text-quickbooks-blue'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setViewMode('yearly')}
              className={`px-3 py-1 text-sm font-medium rounded-md transition-colors duration-200 ${
                viewMode === 'yearly'
                  ? 'bg-white text-quickbooks-blue shadow-quickbooks'
                  : 'text-quickbooks-gray-dark hover:text-quickbooks-blue'
              }`}
            >
              Yearly
            </button>
          </div>
        </div>
        
        <div className="text-sm text-quickbooks-gray-dark">
          Total Policies: {policies.length}
        </div>
      </div>

      {/* Policies Table */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-quickbooks-gray border-b border-quickbooks-border">
              <th className="text-left p-3 font-semibold text-gray-900">Policy Details</th>
              <th className="text-left p-3 font-semibold text-gray-900">Premium</th>
              <th className="text-left p-3 font-semibold text-gray-900">Frequency</th>
              <th className="text-left p-3 font-semibold text-gray-900">Period</th>
              <th className="text-left p-3 font-semibold text-gray-900">Effective Dates</th>
              <th className="text-left p-3 font-semibold text-gray-900">Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentPolicies.map((policy) => {
              const schedule = generatePaymentSchedule(policy);
              const aggregated = aggregatePaymentsByPeriod(schedule, viewMode);
              const isExpanded = expandedRows.has(policy.policy_number);
              
              return (
                <React.Fragment key={policy.policy_number}>
                  {/* Main Policy Row */}
                  <tr className="border-b border-quickbooks-border hover:bg-quickbooks-gray-light">
                    <td className="p-3">
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={() => toggleRow(policy.policy_number)}
                          className="text-quickbooks-gray-dark hover:text-quickbooks-blue"
                        >
                          {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </button>
                        <div>
                          <div className="font-medium text-gray-900">{policy.policy_number}</div>
                          <div className="text-sm text-quickbooks-gray-dark">
                            <User className="inline h-3 w-3 mr-1" />
                            {policy.policyholder_name}
                          </div>
                          {policy.policyholder_name !== policy.insured_name && (
                            <div className="text-sm text-quickbooks-gray-dark">
                              Insured: {policy.insured_name}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="font-medium text-gray-900">
                        {formatCurrency(policy.premium_amount, policy.currency)}
                      </div>
                      <div className="text-sm text-quickbooks-gray-dark">
                        {policy.currency}
                      </div>
                    </td>
                    <td className="p-3">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        policy.premium_frequency === 'monthly'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {policy.premium_frequency}
                      </span>
                    </td>
                    <td className="p-3">
                      <div className="text-gray-900">{policy.total_period}</div>
                      <div className="text-sm text-quickbooks-gray-dark">
                        {policy.premium_frequency === 'monthly' ? 'months' : 'years'}
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="text-sm text-gray-900">
                        <Calendar className="inline h-3 w-3 mr-1" />
                        {formatDate(policy.effective_start_date)}
                      </div>
                      {policy.effective_end_date && (
                        <div className="text-sm text-quickbooks-gray-dark">
                          to {formatDate(policy.effective_end_date)}
                        </div>
                      )}
                    </td>
                    <td className="p-3">
                      <Link
                        to={`/policies/${policy.policy_number}`}
                        className="text-quickbooks-blue hover:text-quickbooks-blue-dark text-sm font-medium"
                      >
                        View Details
                      </Link>
                    </td>
                  </tr>

                  {/* Expanded Payment Schedule Row */}
                  {isExpanded && (
                    <tr className="bg-quickbooks-gray-light">
                      <td colSpan="6" className="p-4">
                        <div className="space-y-4">
                          <h4 className="font-semibold text-gray-900">
                            Payment Schedule ({viewMode === 'monthly' ? 'Monthly' : 'Yearly'} View)
                          </h4>
                          
                          {/* Payment Schedule Table */}
                          <div className="overflow-x-auto">
                            <table className="w-full border-collapse bg-white rounded-lg shadow-quickbooks">
                              <thead>
                                <tr className="bg-quickbooks-gray border-b border-quickbooks-border">
                                  <th className="text-left p-2 text-sm font-medium text-gray-700">
                                    {viewMode === 'monthly' ? 'Month' : 'Year'}
                                  </th>
                                  <th className="text-left p-2 text-sm font-medium text-gray-700">Payment Count</th>
                                  <th className="text-left p-2 text-sm font-medium text-gray-700">Total Amount</th>
                                  <th className="text-left p-2 text-sm font-medium text-gray-700">Policies</th>
                                  <th className="text-left p-2 text-sm font-medium text-gray-700">Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {Object.entries(aggregated)
                                  .sort(([a], [b]) => a.localeCompare(b))
                                  .map(([period, data]) => (
                                    <tr key={period} className="border-b border-quickbooks-border hover:bg-quickbooks-gray-light">
                                      <td className="p-2 text-sm font-medium text-gray-900">
                                        {viewMode === 'monthly' 
                                          ? format(parseISO(period + '-01'), 'MMM yyyy')
                                          : period
                                        }
                                      </td>
                                      <td className="p-2 text-sm text-gray-700">{data.count}</td>
                                      <td className="p-2 text-sm font-medium text-gray-900">
                                        {formatCurrency(data.total, policy.currency)}
                                      </td>
                                      <td className="p-2 text-sm text-gray-700">
                                        {Array.from(data.policies).join(', ')}
                                      </td>
                                      <td className="p-2">
                                        <Link
                                          to={`/policies/${policy.policy_number}`}
                                          className="inline-flex items-center px-3 py-1 bg-enpact-red text-white text-xs font-medium rounded-md hover:bg-enpact-red-dark transition-colors duration-200"
                                        >
                                          <Eye className="h-3 w-3 mr-1" />
                                          View Details
                                        </Link>
                                      </td>
                                    </tr>
                                  ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="card">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Showing {startIndex + 1} to {Math.min(endIndex, totalItems)} of {totalItems} policies
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={goToFirstPage}
                disabled={currentPage === 1}
                className="p-2 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                title="First page"
              >
                <ChevronFirst className="h-4 w-4" />
              </button>
              <button
                onClick={goToPreviousPage}
                disabled={currentPage === 1}
                className="p-2 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                title="Previous page"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              
              <div className="flex items-center space-x-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => goToPage(pageNum)}
                      className={`px-3 py-1 text-sm font-medium rounded-md transition-colors duration-200 ${
                        currentPage === pageNum
                          ? 'bg-quickbooks-blue text-white'
                          : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              
              <button
                onClick={goToNextPage}
                disabled={currentPage === totalPages}
                className="p-2 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                title="Next page"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
              <button
                onClick={goToLastPage}
                disabled={currentPage === totalPages}
                className="p-2 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                title="Last page"
              >
                <ChevronLast className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PolicyTable;
